import React, { useReducer, useEffect, useRef, useState } from 'react';

import { Box } from '@atlaskit/primitives/compiled';

import type { A2UISurfaceProps } from './A2UISurface.types.tsx';
import { ensureComponentsRegistered } from './components/registerAll.ts';
import { DataContext } from './context/DataContext.tsx';
import { SurfaceContext } from './context/SurfaceContext.tsx';
import { processMessage } from './engine/messageProcessor.ts';
import { TreeBuilder } from './engine/TreeBuilder.tsx';
import { surfaceReducer, createInitialSurfaceState } from './state/surfaceStore.ts';

export const A2UISurface = ({
	messages,
	onAction,
	testId,
}: A2UISurfaceProps): React.JSX.Element | null => {
	// Ensure component renderers are registered before first render
	ensureComponentsRegistered();
	const [state, dispatch] = useReducer(surfaceReducer, undefined, createInitialSurfaceState);
	const [processingError, setProcessingError] = useState<string | null>(null);
	const processedCountRef = useRef(0);

	// Process new messages incrementally
	useEffect(() => {
		setProcessingError(null);
		const startIndex = processedCountRef.current;
		for (let i = startIndex; i < messages.length; i++) {
			try {
				const action = processMessage(messages[i]);
				dispatch(action);
			} catch (error) {
				const message = error instanceof Error ? error.message : String(error);
				setProcessingError(`Failed to process message #${i}: ${message}`);
				break;
			}
		}
		processedCountRef.current = messages.length;
	}, [messages]);

	if (processingError) {
		return <Box testId={`${testId ?? 'a2ui-surface'}-processing-error`}>{processingError}</Box>;
	}

	// Don't render until we have a surface
	if (!state.surfaceId || !state.rootId) {
		return null;
	}

	return (
		<SurfaceContext.Provider value={{ state, dispatch, onAction }}>
			<DataContext.Provider value={{ basePath: '' }}>
				<Box testId={testId}>
					<TreeBuilder rootId={state.rootId} />
				</Box>
			</DataContext.Provider>
		</SurfaceContext.Provider>
	);
};
